'use client';
import { useSort } from '@/common/hooks/useSort';
import { useStickyHeader } from '@/hooks/Category/useCategory';
import { useIsMobile } from '@/hooks/use-is-mobile';
import { cn } from '@/lib/utils';
import { useSearchParams } from 'next/navigation';
import { Fragment } from 'react';
import Pagination from '../../components/Pagination';

// import NoProductsFound from '../Category/NoProductsFound';
import { SearchFilterContainer } from '@/components/Category/search-filter-container';
import { useAppContext } from '@/context/app';
import { useRef } from 'react';
import SearchBarClasses from '../ErrorView/errorView.module.css';
import Gallery, { GalleryShimmer } from '../Gallery';
import HomeRecommend from '../HomeRecommend';
import SearchBar from '../SearchBar';
import { useGetFilterData } from './use-get-filter-data';
import { useGetFilterInput } from './use-get-filter-input';
import { useGetPageSize } from './use-get-page-size';
import { useGetSearchData } from './use-get-search-data';
import { useGetSortMethods } from './use-get-sort-methods';

const SearchPageContent = ({ data, loading, error, pageControl, hotProductData }: any) => {
  const { isMobile } = useIsMobile();
  if (!data && loading) {
    return (
      <Fragment>
        <section className=''>
          <GalleryShimmer
            listColumn={isMobile ? 2 : 4}
            items={Array.from({
              length: 12,
            }).fill(null)}
          />
        </section>
      </Fragment>
    );
  }

  if (!data && error) {
    return (
      <div
        aria-live='polite'
        className='flex'
      >
        No results found. The search term may be missing or invalid.
      </div>
    );
  }

  if (!data) {
    return null;
  }

  if (data.products.items.length === 0) {
    const hotData = hotProductData?.CmsHotProducts ? hotProductData?.CmsHotProducts[0]?.items : [];
    // return <NoProductsFound isShowTips={false} />;
    return (
      <div>
        <p className='px-[50px]  text-center font-roboto-medium text-[20px] font-medium leading-[28px] lg_pb-[60px] lg_pt-[30px]'>
          Sorry, no products were found for your search
        </p>
        <HomeRecommend
          hotProductData={hotData}
          title={'You May Also Like'}
        />
      </div>
    );
  }

  return (
    <Fragment>
      <section className='px-[12px] lg_px-0'>
        <Gallery items={data.products.items} />
      </section>
      <section className=''>
        <Pagination pageControl={pageControl} />
      </section>
    </Fragment>
  );
};

// 修改后的 SearchPage 组件
export const SearchPage = ({ hotProductData, categoryData }: { hotProductData: any; categoryData: any }) => {
  const { allCategories } = categoryData;

  const { aggregations, items, sort_fields } = allCategories;

  const { options: sortFields } = sort_fields;
  const searchParams = useSearchParams();
  const searchTerm = searchParams.get('query');
  const { availableSortMethods, sortLoading } = useGetSortMethods();
  const { pageSize } = useGetPageSize();
  const [{ isSticky, isFilterSticky }] = useAppContext();
  const { isMobile } = useIsMobile();
  // const pathname = window.location.pathname;
  // const categoryUrlKey = pathname?.split('/')?.[0] ?? pathname?.split('/')?.[1];

  const { introspectionCalled, introspectionLoading, newFilters } = useGetFilterInput();
  const sortProps = useSort();
  const [currentSort] = sortProps;
  const { sortAttribute, sortDirection } = currentSort as any;
  const { pageControl, searchCalled, searchLoading, data, error } = useGetSearchData({
    pageSize,
    sortDirection,
    sortAttribute,
    newFilters,
  });
  const { filters, shouldShowFilterButtons, shouldShowFilterShimmer, filterLoading } = useGetFilterData();
  const filterContainerRef = useRef(null);
  useStickyHeader(filterContainerRef);

  const loading = (introspectionCalled && !searchCalled) || searchLoading || introspectionLoading;

  const productsCount = data && data.products && data.products.total_count ? data.products.total_count : 0;

  // If there are no products we can hide the sort button.
  const shouldShowSortButtons = productsCount && availableSortMethods;
  const shouldShowSortShimmer = !productsCount && loading;

  return (
    <article className='container mx-auto py-[40px] lg_py-[60px]'>
      <div className='relative z-0 mb-[20px] px-[12px] lg_mb-[40px] lg_px-0'>
        <SearchBar
          isOpen
          showPoplarSearches={true}
          showAutoComplete={true}
          classes={{
            root_open: 'py-0',
            autocomplete: SearchBarClasses.autocomplete,
            form: SearchBarClasses.form,
            container: 'w-full',
            search: SearchBarClasses.search,
          }}
        />
      </div>
      {/* <div className='pb-[20px] font-oswald-bold text-[1.125rem]'>{`${productsCount} Search results for '${searchTerm}'`}</div> */}
      <div className='lg_grow'>
        <div
          ref={filterContainerRef}
          className={cn(
            {
              'fixed left-0 right-0 z-50 bg-white shadow-md': isFilterSticky,
              relative: !isFilterSticky,
            },
            isSticky && isFilterSticky ? (isMobile ? 'top-[49px]' : 'top-[74px]') : 'top-0',
          )}
        >
          <div
            className={cn(
              {
                'container mx-auto ': isFilterSticky,
              },
              isFilterSticky ? (isMobile ? 'px-[12px] pt-[20px]' : 'pt-[20px]') : '',
            )}
          >
            <SearchFilterContainer
              aggregations={aggregations}
              sortFields={sortFields} //
              totalCount={productsCount ?? 0}
              currentCurrencyCode={'USD'}
              categoryUrlKey={'mens-boots'} //分类名，没有
              filters={filters}
              shouldShowFilterButtons={shouldShowFilterButtons}
              shouldShowFilterShimmer={shouldShowFilterShimmer}
              filterLoading={filterLoading}
              availableSortMethods={availableSortMethods}
              sortLoading={sortLoading}
            />
          </div>
        </div>
        {/* <div
          className={cn(
            'flex',
            'flex-wrap',
            'justify-center',
            'lg_flex-nowrap',
            'lg_items-center',
            'lg_justify-between',
          )}
        >
          <div className='mx-0 mb-[10px] flex basis-full justify-center lg_justify-start'>
            {shouldShowSortButtons ? (
              availableSortMethods && (
                <ProductSortMobile
                  sortProps={sortProps}
                  availableSortMethods={availableSortMethods}
                />
              )
            ) : shouldShowSortShimmer ? (
              <ProductSortShimmer />
            ) : null}
          </div>
        </div> */}
        <SearchPageContent
          data={data}
          loading={loading}
          error={error}
          pageControl={pageControl}
          hotProductData={hotProductData}
        />
      </div>
    </article>
  );
};
